// FunctionalSpecification.js — Documentation Fonctionnelle (HUB).
// Assemble les POC interactifs des écrans des 3 modules (Environnement / Campagne / Performances).
// Navigation : Module -> Écran -> rend le composant POC correspondant (réplique IHM + guide utilisateur).
// Chaque écran est un composant autonome dans src/components/functional/*POC.js.
import React, { useState } from 'react';
import { Icon } from '../components/ui';

// Environnement
import AgentPOC from '../components/functional/AgentPOC';
import EnvironmentsPOC from '../components/functional/EnvironmentsPOC';
import FunctionsPOC from '../components/functional/FunctionsPOC';
import TasksPOC from '../components/functional/TasksPOC';
import RecordPOC from '../components/functional/RecordPOC';
import SupervisionPOC from '../components/functional/SupervisionPOC';
import AnomaliesPOC from '../components/functional/AnomaliesPOC';
import EvolutionsPOC from '../components/functional/EvolutionsPOC';
import TaskImportPOC from '../components/functional/TaskImportPOC';
// Campagne
import CampaignManagerPOC from '../components/functional/CampaignManagerPOC';
import TestCaseLibraryPOC from '../components/functional/TestCaseLibraryPOC';
import ExecutionCenterPOC from '../components/functional/ExecutionCenterPOC';
import CampaignSupervisionPOC from '../components/functional/CampaignSupervisionPOC';
import RapportsPOC from '../components/functional/RapportsPOC';
// Performances
import TestPlanDesignerPOC from '../components/functional/TestPlanDesignerPOC';
import ExecutionMonitoringPOC from '../components/functional/ExecutionMonitoringPOC';
import PerfReportsPOC from '../components/functional/PerfReportsPOC';

const MODULES = [
  {
    key: 'ENVIRONMENT', label: 'Environnement', color: '#10B981', icon: 'Globe',
    screens: [
      { key: 'agents', title: 'Agents', Comp: AgentPOC },
      { key: 'environments', title: 'Environnements', Comp: EnvironmentsPOC },
      { key: 'functions', title: 'Fonctions', Comp: FunctionsPOC },
      { key: 'tasks', title: 'Tâches', Comp: TasksPOC },
      { key: 'record', title: 'Enregistrement', Comp: RecordPOC },
      { key: 'supervision', title: 'Supervision', Comp: SupervisionPOC },
      { key: 'anomalies', title: 'Anomalies', Comp: AnomaliesPOC },
      { key: 'evolutions', title: 'Évolutions', Comp: EvolutionsPOC },
      { key: 'taskImport', title: 'Import de tâches', Comp: TaskImportPOC },
    ],
  },
  {
    key: 'CAMPAIGN', label: 'Campagne', color: '#F59E0B', icon: 'Target',
    screens: [
      { key: 'campaigns', title: 'Campagnes', Comp: CampaignManagerPOC },
      { key: 'testcases', title: 'Cas de test', Comp: TestCaseLibraryPOC },
      { key: 'execution', title: "Centre d'exécution", Comp: ExecutionCenterPOC },
      { key: 'supervision', title: 'Supervision', Comp: CampaignSupervisionPOC },
      { key: 'rapports', title: 'Rapports', Comp: RapportsPOC },
    ],
  },
  {
    key: 'PERFORMANCE', label: 'Performances', color: '#EF4444', icon: 'TrendingUp',
    screens: [
      { key: 'plans', title: 'Plans de test', Comp: TestPlanDesignerPOC },
      { key: 'monitoring', title: "Monitoring d'exécution", Comp: ExecutionMonitoringPOC },
      { key: 'reports', title: 'Rapports', Comp: PerfReportsPOC },
    ],
  },
];

const FunctionalSpecification = () => {
  const [moduleKey, setModuleKey] = useState(MODULES[0].key);
  const [screenKey, setScreenKey] = useState(MODULES[0].screens[0].key);

  const module = MODULES.find((m) => m.key === moduleKey) || MODULES[0];
  const screen = module.screens.find((s) => s.key === screenKey) || module.screens[0];
  const ScreenComp = screen.Comp;

  const selectModule = (m) => { setModuleKey(m.key); setScreenKey(m.screens[0].key); };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Documentation Fonctionnelle</h1>
        <p className="mt-1 text-gray-600 dark:text-gray-300">POC interactifs des écrans, par module. Sélectionnez un module puis un écran — chaque écran propose une réplique de l'IHM et un guide pas-à-pas.</p>
      </div>

      {/* Sélecteur de module */}
      <div className="flex flex-wrap gap-2">
        {MODULES.map((m) => {
          const on = m.key === moduleKey;
          return (
            <button key={m.key} onClick={() => selectModule(m)}
              style={on ? { backgroundColor: m.color, borderColor: m.color, color: '#fff' } : undefined}
              className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 border transition-colors ${on ? '' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
              <Icon name={m.icon} className="h-4 w-4" />{m.label}
              <span className={`text-xs ${on ? 'text-white/80' : 'text-gray-400'}`}>({m.screens.length})</span>
            </button>
          );
        })}
      </div>

      {/* Sélecteur d'écran */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-700 pb-3">
        {module.screens.map((s) => {
          const on = s.key === screenKey;
          return (
            <button key={s.key} onClick={() => setScreenKey(s.key)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${on ? 'bg-blue-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
              {s.title}
            </button>
          );
        })}
      </div>

      {/* Écran sélectionné (POC) */}
      <div key={`${moduleKey}-${screenKey}`}>
        <ScreenComp />
      </div>
    </div>
  );
};

export default FunctionalSpecification;
